import type { ApiResponse, FilterOptions, DeviceReportResponse, SoftwareReportResponse, SoftwareReportFilters, SoftwareDevicesResponse, SoftwareByDeviceResponse, UserSessionDailyReportResponse, UserSessionDailyReportFilters, UserSessionDayEventsResponse, UserSessionDayEventsFilters, EventReportResponse, GeolocationReportResponse, GeolocationReportFilters, LocationHistoryResponse, LocationHistoryFilters, EventReportFilters, EventHistoryResponse, EventHistoryFilters } from "@/shared/types/types";

type SoftwareQueryOptions = Partial<{
  search: string;
  clientId: string;
  groupId: string;
  version: string;
  sort: string;
  order: string;
  page: number;
  pageSize: number;
}>;

function buildSoftwareQuery(options?: SoftwareQueryOptions): string {
  if (!options) return "";
  const params = new URLSearchParams();
  if (options.search) params.append("search", options.search);
  if (options.groupId) params.append("groupId", options.groupId);
  if (options.clientId) params.append("clientId", options.clientId);
  if (options.version) params.append("version", options.version);
  if (options.sort) params.append("sort", options.sort);
  if (options.order) params.append("order", options.order);
  if (options.page) params.append("page", String(options.page));
  if (options.pageSize) params.append("pageSize", String(options.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildUserSessionDailyQuery(options?: UserSessionDailyReportFilters): string {
  if (!options) return "";
  const params = new URLSearchParams();
  if (options.deviceId) params.append("deviceId", options.deviceId);
  if (options.groupId) params.append("groupId", options.groupId);
  if (options.clientId) params.append("clientId", options.clientId);
  if (options.startDate) params.append("startDate", options.startDate);
  if (options.endDate) params.append("endDate", options.endDate);
  if (options.page) params.append("page", String(options.page));
  if (options.pageSize) params.append("pageSize", String(options.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildEventReportQuery(options?: EventReportFilters): string {
  if (!options) return "";
  const params = new URLSearchParams();
  if (options.groupId) params.append("groupId", options.groupId);
  if (options.clientId) params.append("clientId", options.clientId);
  if (options.startDate) params.append("startDate", options.startDate);
  if (options.endDate) params.append("endDate", options.endDate);
  if (options.page) params.append("page", String(options.page));
  if (options.pageSize) params.append("pageSize", String(options.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildGeolocationQuery(options?: GeolocationReportFilters): string {
  if (!options) return "";
  const params = new URLSearchParams();
  if (options.deviceId) params.append("deviceId", options.deviceId);
  if (options.groupId) params.append("groupId", options.groupId);
  if (options.clientId) params.append("clientId", options.clientId);
  if (options.days !== undefined) params.append("days", String(options.days));
  if (options.page) params.append("page", String(options.page));
  if (options.pageSize) params.append("pageSize", String(options.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

type Request = <T>(endpoint: string, options?: RequestInit) => Promise<T>;

export function createReportsApi(request: Request, buildQueryString: (options?: FilterOptions) => string) {
  return {
    getDevicesReport: (options?: FilterOptions): Promise<ApiResponse<DeviceReportResponse>> =>
      request<ApiResponse<DeviceReportResponse>>(`/reports/devices${buildQueryString(options)}`),

    // Software endpoints recebem `pageSize` direto (e nao `limit` como o resto), entao
    // usam construcao propria — buildQueryString mapeia `limit` para `pageSize`.
    getSoftwareReport: (options?: SoftwareReportFilters): Promise<ApiResponse<SoftwareReportResponse>> =>
      request<ApiResponse<SoftwareReportResponse>>(
        `/reports/software${buildSoftwareQuery(options)}`
      ),

    getSoftwareDevices: (
      softwareId: number,
      options?: { search?: string; version?: string; clientId?: string; groupId?: string; page?: number; pageSize?: number }
    ): Promise<ApiResponse<SoftwareDevicesResponse>> =>
      request<ApiResponse<SoftwareDevicesResponse>>(
        `/reports/software/${softwareId}/devices${buildSoftwareQuery(options)}`
      ),

    getSoftwareByDevice: (
      options?: { search?: string; clientId?: string; groupId?: string; page?: number; pageSize?: number }
    ): Promise<ApiResponse<SoftwareByDeviceResponse>> =>
      request<ApiResponse<SoftwareByDeviceResponse>>(
        `/reports/software/by-device${buildSoftwareQuery(options)}`
      ),

    getUserSessionsDailyReport: (
      options?: UserSessionDailyReportFilters
    ): Promise<ApiResponse<UserSessionDailyReportResponse>> =>
      request<ApiResponse<UserSessionDailyReportResponse>>(
        `/reports/user-sessions${buildUserSessionDailyQuery(options)}`
      ),

    getUserSessionDayEvents: (
      options: UserSessionDayEventsFilters
    ): Promise<ApiResponse<UserSessionDayEventsResponse>> => {
      const params = new URLSearchParams();
      params.append("deviceId", options.deviceId);
      params.append("userName", options.userName);
      params.append("day", options.day);
      if (options.domainName) params.append("domainName", options.domainName);
      if (options.clientId) params.append("clientId", options.clientId);
      return request<ApiResponse<UserSessionDayEventsResponse>>(
        `/reports/user-sessions/events?${params.toString()}`
      );
    },

    getEventsReport: (
      options?: EventReportFilters
    ): Promise<ApiResponse<EventReportResponse>> =>
      request<ApiResponse<EventReportResponse>>(
        `/reports/events${buildEventReportQuery(options)}`
      ),

    getEventHistory: (
      options: EventHistoryFilters
    ): Promise<ApiResponse<EventHistoryResponse>> => {
      const params = new URLSearchParams();
      params.append("deviceId", options.deviceId);
      params.append("hours", String(options.hours));
      params.append("metrics", options.metrics.join(","));
      params.append("bucketSeconds", String(options.bucketSeconds));
      return request<ApiResponse<EventHistoryResponse>>(
        `/reports/event-history?${params.toString()}`
      );
    },

    getGeolocationReport: (
      options?: GeolocationReportFilters
    ): Promise<ApiResponse<GeolocationReportResponse>> =>
      request<ApiResponse<GeolocationReportResponse>>(
        `/reports/geolocation${buildGeolocationQuery(options)}`
      ),

    getDeviceLocationHistory: (
      deviceId: string,
      options?: LocationHistoryFilters
    ): Promise<ApiResponse<LocationHistoryResponse>> => {
      const params = new URLSearchParams();
      if (options?.days !== undefined) params.append("days", String(options.days));
      if (options?.limit !== undefined) params.append("limit", String(options.limit));
      const qs = params.toString();
      return request<ApiResponse<LocationHistoryResponse>>(
        `/reports/geolocation/${encodeURIComponent(deviceId)}/history${qs ? `?${qs}` : ""}`
      );
    }
  };
}
